import { useEffect, useState } from "react";
import axiosInstance from "@/lib/axios";
import { callLogEndpoint } from "@/config/endpoints";
import { CallRecordingPlayer } from "@/features/Dashboard/v2/activity/CallRecordingPlayer";
import { AIToolCallRecord } from "../types/ai.types";

// ---------------------------------------------------------------------------
// RecordingCards — inline call-recording players for an assistant turn.
//
//   • play_call_recording → one auto-playing player (the model announces it).
//     The tool result carries NO url (provider links can hold credentials and
//     must not sit in the model's context or the chat transcript); the URL is
//     fetched here through the scoped /call-logs/:id/recording-url endpoint.
//   • list_call_recordings → the listed calls, each with the same on-demand
//     player the activity feed uses (URL fetched on first Play through the
//     scoped /call-logs/:id/recording-url endpoint)
//
// Rendered from the turn's tool results, so the audio sits right under the
// answer instead of buried in the tool-chip JSON.
// ---------------------------------------------------------------------------

interface ListedCall {
  callId: number;
  at?: string;
  outcome?: string;
  durationSec?: number;
  leadName?: string | null;
  callerName?: string | null;
  hasRecording?: boolean;
}

export default function RecordingCards({ toolCalls }: { toolCalls: AIToolCallRecord[] }) {
  const plays = toolCalls.filter((t) => t.name === "play_call_recording" && t.result?.action === "play" && t.result?.callId);
  const lists = toolCalls.filter((t) => t.name === "list_call_recordings" && Array.isArray(t.result?.rows) && t.result.rows.length);
  if (!plays.length && !lists.length) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, margin: "6px 0" }}>
      {plays.map((t) => (
        <div key={t.id} style={card}>
          <div style={title}>🎧 {t.result.title ?? `Call #${t.result.callId}`}</div>
          <AutoPlayRecording key={Number(t.result.callId)} callId={Number(t.result.callId)} />
        </div>
      ))}
      {lists.map((t) => (
        <div key={t.id} style={card}>
          <div style={title}>Recordings</div>
          {(t.result.rows as ListedCall[]).filter((r) => r.hasRecording !== false).slice(0, 10).map((r) => (
            <div key={r.callId} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", borderTop: "1px solid #00000010" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {r.leadName ?? "Unknown lead"}
                  {r.callerName ? ` · ${r.callerName}` : ""}
                </div>
                <div style={{ fontSize: 11, opacity: 0.6 }}>
                  {r.at ?? ""}
                  {r.outcome ? ` · ${r.outcome}` : ""}
                </div>
              </div>
              <div style={{ width: 170, flex: "0 0 auto" }}>
                <CallRecordingPlayer callLogId={r.callId} fallbackDurationSec={r.durationSec ?? 0} />
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/** Fetches the scoped recording URL on mount and auto-plays it. */
function AutoPlayRecording({ callId }: { callId: number }) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Keyed by callId in the parent, so a new call mounts fresh state — no
  // synchronous resets inside the effect.
  useEffect(() => {
    let cancelled = false;
    axiosInstance
      .get(`${callLogEndpoint}/${callId}/recording-url`)
      .then((res) => {
        if (cancelled) return;
        const u = res.data?.recordingUrl;
        if (u) setUrl(u);
        else setError("No recording is available for this call.");
      })
      .catch(() => {
        if (!cancelled) setError("Could not load this recording.");
      });
    return () => {
      cancelled = true;
    };
  }, [callId]);
  if (error) return <div style={{ fontSize: 12, opacity: 0.7 }}>{error}</div>;
  if (!url) return <div style={{ fontSize: 12, opacity: 0.7 }}>Loading recording…</div>;
  return <audio src={url} controls autoPlay style={{ width: "100%" }} />;
}

const card: React.CSSProperties = {
  border: "1px solid var(--color-black-3, #e5e7eb)",
  borderRadius: 10,
  padding: "8px 10px",
  background: "var(--color-bg, #fff)",
};
const title: React.CSSProperties = { fontSize: 12, fontWeight: 600, marginBottom: 6, color: "var(--color-text-primary, #1a1a1a)" };
